import { publishedContactEmail } from "@app/shared";
import {
  type AnyRoute,
  createRoute,
  type ErrorComponentProps,
  Outlet,
  type RouteComponent,
  redirect,
  useRouter,
} from "@tanstack/react-router";
import {
  type ComponentType,
  lazy,
  type ReactElement,
  Suspense,
  useCallback,
  useEffect,
  useState,
} from "react";
import { appConfig } from "../../../../app.config";
import { Button } from "../components/ui/button";
import { rootRoute } from "../root-route";
import { carteRoute } from "./cartes-publique/route";
import { cartesRoutes } from "./cartes-regie/routes-cartes";
import {
  DIFFUSION_CHARGEMENT,
  DIFFUSION_INDISPONIBLE,
  DIFFUSION_PATH,
  DIFFUSION_RECHARGER,
  isOnglet,
  ONGLET_PAR_DEFAUT,
  type Onglet,
  RESEAUX_DE_RETOUR,
  type RetoursAutorisation,
  texteOuRien,
} from "./diffusion-entree";
import {
  EN_DIRECT_CHARGEMENT,
  EN_DIRECT_INDISPONIBLE,
  VIEWER_PATH,
} from "./direct/en-direct-entree";
import { FicheViewer } from "./direct/fiche-viewer";
import { EssaiOverlay } from "./overlay/essai";
import { SceneOverlay } from "./overlay/scene";
import { TaverneOverlay } from "./overlay/taverne";
import { aPosterRoute, planningRoute } from "./planning/routes-planning";
import { rechargerUneFois } from "./rechargement";
import { recompensesRoute } from "./recompenses/route";
import { REGIE_PATH, REGLAGES_TWITCH_PATH, RegiePage } from "./regie";
import { CleOverlay } from "./reglages/cle-overlay";
import { ImportPage } from "./reglages/import";
import { ReglagesPage } from "./reglages/index";
import { estUnLundi } from "./semaine-dates";
import { useSessionGlissante } from "./session-glisse";
import { AccueilPage } from "./taverne/accueil";
import {
  CHARGEMENT_DE_L_ENTREE,
  ENTREE_INDISPONIBLE,
  ENTREE_RECHARGER,
} from "./taverne/entree-texts";
import type { SortieDeLaPage } from "./taverne/moi";
import type { AdresseLue, SortieDeRejoindre } from "./taverne/rejoindre";
import { suiteSure } from "./taverne/suite";
import { ClassementPage } from "./viewers/classement";

/**
 * Routes de l'app. Fichier possede par l'app : ajoute ici tes pages, le template
 * ne reecrira pas ce fichier (_skip_if_exists).
 */
/**
 * L'accueil public de La Taverne (`taverne/accueil.tsx`) : ouvert a tous, sans session.
 *
 * **`/?error=<code>` renvoie sur `/rejoindre?error=<code>`** (tour 1 de la PR 6, M1). Plus de dix minutes sur la page de Twitch, ou un
 * vieil onglet : la ligne d'etat de Better Auth a expire, il n'a plus `errorCallbackURL` et ramene la personne sur la racine du site
 * (`onAPIError.errorURL`, `lab/auth/index.ts`). L'accueil ne lisait pas `error` : la personne se croyait peut-etre connectee, et rien ne
 * le lui disait. Seul le code part ; le texte libre de Better Auth (`error_description`) et les autres parametres de l'accueil restent ici.
 * Le routeur remplace toujours l'entree d'historique d'un renvoi (`followRedirect`, router-core) : `/?error=` ne reste pas derriere.
 */
export const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: ({ location }) => {
    const error = texteDeLAdresse((location.search as Record<string, unknown>).error);
    if (error !== undefined && error !== "") {
      throw redirect({ to: "/rejoindre", search: { error } });
    }
  },
  component: AccueilPage,
});

/**
 * La regie : l'ecran En direct (`direct/en-direct.tsx`, livre a part comme celui de la diffusion, voir plus bas). Deux
 * codes y arrivent encore par l'adresse, parce que le backend y renvoie Nicolas :
 * - `?twitch=<code>` : apres l'autorisation d'un compte Twitch ;
 * - `?error=<code>` : Better Auth y ramene un lien magique perime, deja servi ou refuse.
 * Ils appartiennent a la page des comptes Twitch (`regieTwitchRoute`) : cette route les lui passe tels quels. L'adresse
 * est a n'importe qui, et le routeur lit ses valeurs comme du JSON (`?twitch=123` donne un nombre) : on ne garde que du
 * texte, que la page confronte ensuite a des listes fermees.
 */
export const regieRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: REGIE_PATH,
  validateSearch: (search: Record<string, unknown>): { twitch?: string; error?: string } => ({
    ...(typeof search.twitch === "string" ? { twitch: search.twitch } : {}),
    ...(typeof search.error === "string" ? { error: search.error } : {}),
  }),
  component: EnDirectRoute,
  errorComponent: ErreurDeEnDirect,
});

/**
 * Les comptes Twitch, la liaison, l'agent du PC, l'essai d'OBS et l'opposition : l'ancienne page de la regie
 * (`regie.tsx`), deplacee ici par la PR E du lot Q1. Memes deux codes qu'au-dessus.
 */
export const regieTwitchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: REGLAGES_TWITCH_PATH,
  validateSearch: (search: Record<string, unknown>): { twitch?: string; error?: string } => ({
    ...(typeof search.twitch === "string" ? { twitch: search.twitch } : {}),
    ...(typeof search.error === "string" ? { error: search.error } : {}),
  }),
  component: RegieTwitchRoute,
});

/**
 * Lit les deux codes, les retire de l'adresse, et les garde pour la page.
 *
 * Retires, pour qu'un rechargement ne reaffiche pas un « ok » perime ni un lien deja signale mort.
 * Gardes, parce que le routeur relit l'adresse a chaque changement : une page qui lirait la
 * recherche apres le nettoyage ne verrait plus rien, et sa phrase disparaitrait aussitot. D'ou cet
 * etat, pose une fois au montage avec ce que l'adresse portait. Le nettoyage passe par le routeur
 * (`navigate`, qui remplace l'entree d'historique) : un `history.replaceState` direct est relu par
 * le routeur comme n'importe quel changement d'adresse, et donne le meme resultat.
 *
 * Ce composant est la, et non `RegiePage`, parce que celle-ci se monte sans routeur dans ses tests.
 * Son type de retour est ecrit : sans lui, TypeScript infererait celui de `regieTwitchRoute` depuis ce
 * composant, qui lit `regieTwitchRoute`.
 */
function RegieTwitchRoute(): ReactElement {
  const search = regieTwitchRoute.useSearch();
  const navigate = regieTwitchRoute.useNavigate();
  const [seen] = useState(() => ({ twitch: search.twitch, error: search.error }));
  const carriesCode = search.twitch !== undefined || search.error !== undefined;

  useEffect(() => {
    if (carriesCode) {
      // `search: {}` remplace tout, `error_description` compris (le texte libre de Better Auth).
      void navigate({ search: {}, replace: true });
    }
  }, [carriesCode, navigate]);

  return <RegiePage twitch={seen.twitch} linkError={seen.error} />;
}

/**
 * L'ecran de la diffusion est livre a part : ses cinq onglets et leurs phrases pesaient pres de 50 ko du paquet
 * d'entree, que Vite signale au-dela de 500 ko. Il se charge quand on ouvre la page (`Suspense` ci-dessous dit
 * « Chargement... » en attendant). Tant que ce fichier le charge par `import()`, rien de `diffusion.tsx` ni de
 * `diffusion-texts.ts` ne doit etre importe a la lecture depuis le paquet d'entree : ce que la regie et la route
 * en lisent vit dans `diffusion-entree.ts` (`routes.test.tsx` le verifie).
 */
const DiffusionPage = lazy(() =>
  import("./diffusion").then(
    (module) => ({ default: module.DiffusionPage }),
    (cause: unknown) => {
      throw new ChargementEchoue(cause);
    },
  ),
);

/**
 * Le fichier de l'ecran n'est pas arrive : l'`import()` a ete rejete. C'est la seule erreur que `ErreurDeLaDiffusion`
 * traite ; toute autre (un bug de l'ecran, un rendu qui plante) suit le chemin d'erreur normal du routeur.
 */
class ChargementEchoue extends Error {
  constructor(cause: unknown) {
    super("Chargement de l'ecran de la diffusion echoue", { cause });
    this.name = "ChargementEchoue";
  }
}

const RECHARGER_LA_PAGE = (): void => window.location.reload();

/**
 * Ce que montre la route quand le fichier de l'ecran n'arrive pas (le plus souvent parce que l'app a ete mise a jour
 * depuis l'ouverture de l'onglet, et que les noms de fichier ont change) : la phrase, et le bouton qui recharge la
 * page. `recharger` est la pour les tests ; la route n'en passe pas.
 */
export function DiffusionIndisponible({
  recharger = RECHARGER_LA_PAGE,
}: {
  recharger?: () => void;
}): ReactElement {
  // Un rechargement tout seul, une fois par session de l'onglet (`rechargement.ts`) : le plus souvent il suffit. Le
  // bouton, lui, recharge toujours : c'est le geste de Nicolas.
  // biome-ignore lint/correctness/useExhaustiveDependencies: une seule fois, au montage de l'ecran d'echec
  useEffect(() => {
    rechargerUneFois(recharger);
  }, []);
  return (
    <section role="alert" className="flex flex-col items-start gap-3">
      <p>{DIFFUSION_INDISPONIBLE}</p>
      <Button onClick={recharger}>{DIFFUSION_RECHARGER}</Button>
    </section>
  );
}

/**
 * Le composant d'erreur de la route : l'ecran d'echec de chargement pour `ChargementEchoue`, et rien d'autre. Une autre
 * erreur est relancee, le routeur la remonte a son composant d'erreur par defaut : elle ne se fait pas passer pour un
 * fichier manquant, et ne declenche aucun rechargement.
 */
function ErreurDeLaDiffusion({ error }: ErrorComponentProps): ReactElement {
  if (!(error instanceof ChargementEchoue)) throw error;
  return <DiffusionIndisponible />;
}

/**
 * L'ecran En direct est livre a part, comme celui de la diffusion : il porte le client du flux, le titre et le jeu, les
 * secours et leurs phrases, et le paquet d'entree etait deja a la limite des 500 ko que Vite signale. Ce que la route lit
 * sans le charger vit dans `direct/en-direct-entree.ts`.
 */
const EnDirectPage = lazy(() =>
  import("./direct/en-direct").then(
    (module) => ({ default: module.EnDirectPage }),
    (cause: unknown) => {
      throw new ChargementEnDirectEchoue(cause);
    },
  ),
);

/** Le fichier de l'ecran En direct n'est pas arrive : la seule erreur que `ErreurDeEnDirect` traite. */
class ChargementEnDirectEchoue extends Error {
  constructor(cause: unknown) {
    super("Chargement de l'ecran En direct echoue", { cause });
    this.name = "ChargementEnDirectEchoue";
  }
}

/** Comme `DiffusionIndisponible`, pour l'ecran En direct : un rechargement tout seul, une fois, puis le bouton. */
export function EnDirectIndisponible({
  recharger = RECHARGER_LA_PAGE,
}: {
  recharger?: () => void;
}): ReactElement {
  // biome-ignore lint/correctness/useExhaustiveDependencies: une seule fois, au montage de l'ecran d'echec
  useEffect(() => {
    rechargerUneFois(recharger);
  }, []);
  return (
    <section role="alert" className="flex flex-col items-start gap-3">
      <p>{EN_DIRECT_INDISPONIBLE}</p>
      <Button onClick={recharger}>{DIFFUSION_RECHARGER}</Button>
    </section>
  );
}

function ErreurDeEnDirect({ error }: ErrorComponentProps): ReactElement {
  if (!(error instanceof ChargementEnDirectEchoue)) throw error;
  return <EnDirectIndisponible />;
}

/**
 * La regie (`/regie`). Un retour de Twitch ou d'un lien magique en echec (`?twitch=`, `?error=`) est renvoye tel quel a la
 * page des comptes Twitch : le backend n'a pas change d'adresse de retour. Sinon l'ecran En direct, qui mene a la fiche d'un
 * viewer par `/regie/viewers/$id` au telephone (la navigation du routeur, sans recharger la page).
 */
function EnDirectRoute(): ReactElement | null {
  const search = regieRoute.useSearch();
  const navigate = regieRoute.useNavigate();
  const router = useRouter();
  const { twitch, error } = search;
  const renvoi = twitch !== undefined || error !== undefined;

  useEffect(() => {
    if (renvoi) {
      void navigate({
        to: REGLAGES_TWITCH_PATH,
        search: {
          ...(twitch === undefined ? {} : { twitch }),
          ...(error === undefined ? {} : { error }),
        },
        replace: true,
      });
    }
  }, [renvoi, twitch, error, navigate]);

  if (renvoi) return null;
  return (
    <Suspense
      fallback={
        <p role="status" className="text-sm text-muted-foreground">
          {EN_DIRECT_CHARGEMENT}
        </p>
      }
    >
      <EnDirectPage
        ouvrirFiche={(id) => router.history.push(`${VIEWER_PATH}/${encodeURIComponent(id)}`)}
      />
    </Suspense>
  );
}

/**
 * L'ecran « Diffusion » de la regie (`diffusion.tsx`). Trois parametres y arrivent par l'adresse :
 * - `?onglet=<nom>` : l'onglet montre. L'une des six valeurs de `ONGLETS` (`semaine`, `stock`, `suivi`,
 *   `chiffres`, `reseaux`, `reglages`), valide ici, et chacune a son ecran ; une autre valeur est ignoree et l'ecran montre le
 *   suivi ;
 * - `?lundi=<AAAA-MM-JJ>` : la semaine que l'onglet « La semaine » montre. Une date du calendrier qui tombe un
 *   lundi (`estUnLundi`), valide ici ; toute autre valeur est ignoree et l'onglet montre la semaine que l'API rend par
 *   defaut (la suivante). C'est ce que les messages du salon des brouillons ouvrent. Il reste dans l'adresse quand
 *   on change d'onglet, pour retrouver la meme semaine en revenant ;
 * - `?youtube=<code>`, `?instagram=`, `?facebook=`, `?threads=`, `?tiktok=` : le backend y renvoie Nicolas apres
 *   l'autorisation du reseau, avec `onglet=reseaux`. Comme `?twitch=` de la regie, chacun est lu une fois puis
 *   retire de l'adresse.
 * L'adresse est a n'importe qui, et le routeur lit ses valeurs comme du JSON (`?youtube=123` donne un
 * nombre) : chaque code est ramene a du texte, que la page confronte ensuite a la liste fermee du contrat, et
 * qui est retire de l'adresse quelle que soit sa valeur. Une valeur d'`onglet` ou de `lundi` hors de ce qui est
 * admis devient `undefined` : le routeur fusionne la recherche brute de l'adresse et celle que la route valide,
 * donc une cle simplement omise resterait lisible (et `?onglet=inconnu` survivrait au nettoyage de l'adresse).
 */
export const diffusionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: DIFFUSION_PATH,
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    onglet?: Onglet | undefined;
    lundi?: string | undefined;
  } & RetoursAutorisation => ({
    onglet: isOnglet(search.onglet) ? search.onglet : undefined,
    lundi: estUnLundi(search.lundi) ? search.lundi : undefined,
    youtube: texteOuRien(search.youtube),
    instagram: texteOuRien(search.instagram),
    facebook: texteOuRien(search.facebook),
    threads: texteOuRien(search.threads),
    tiktok: texteOuRien(search.tiktok),
  }),
  component: DiffusionRoute,
  errorComponent: ErreurDeLaDiffusion,
});

/**
 * La recherche de l'adresse sans le code de YouTube : l'onglet et le lundi, quand ils y sont. Une cle absente, pas une
 * cle `undefined` (le routeur ne laisserait pas la seconde disparaitre de l'adresse).
 */
const recherche = (onglet: Onglet | undefined, lundi: string | undefined) => ({
  ...(onglet === undefined ? {} : { onglet }),
  ...(lundi === undefined ? {} : { lundi }),
});

/**
 * Lit le code de retour de YouTube, le retire de l'adresse (l'onglet et la semaine restent : un rechargement montre
 * le meme ecran), et le garde pour la page. Meme principe que `RegieRoute` : retire, pour qu'un rechargement ne
 * reaffiche pas un « ok » perime ; garde, parce que le routeur relit l'adresse a chaque changement et qu'une
 * page qui lirait la recherche apres le nettoyage ne verrait plus rien. Le choix d'un onglet ou d'une semaine passe
 * par l'adresse : la route le pose, la page ne sait pas ce qu'est une adresse. Changer d'onglet garde le lundi ;
 * choisir une semaine passe sur l'onglet « La semaine », sans lundi pour « La semaine prochaine ».
 */
function DiffusionRoute(): ReactElement {
  const search = diffusionRoute.useSearch();
  const navigate = diffusionRoute.useNavigate();
  const [seen] = useState<RetoursAutorisation>(() => ({
    youtube: search.youtube,
    instagram: search.instagram,
    facebook: search.facebook,
    threads: search.threads,
    tiktok: search.tiktok,
  }));
  const carriesCode = RESEAUX_DE_RETOUR.some((reseau) => search[reseau] !== undefined);
  // L'onglet et le lundi valides par la route, pas ceux que l'adresse brute porte : une valeur inconnue ne survit pas
  // au nettoyage.
  const { onglet, lundi } = search;

  useEffect(() => {
    if (carriesCode) {
      void navigate({ search: recherche(onglet, lundi), replace: true });
    }
  }, [carriesCode, navigate, onglet, lundi]);

  return (
    <Suspense
      fallback={
        <p role="status" className="text-sm text-muted-foreground">
          {DIFFUSION_CHARGEMENT}
        </p>
      }
    >
      <DiffusionPage
        onglet={onglet ?? ONGLET_PAR_DEFAUT}
        lundi={lundi}
        retours={seen}
        onChoisir={(choisi) => void navigate({ search: recherche(choisi, lundi) })}
        onChoisirSemaine={(choisie) => void navigate({ search: recherche("semaine", choisie) })}
      />
    </Suspense>
  );
}

/**
 * L'overlay d'essai d'OBS : une page nue (`staticData.pageNue`), donc ni en-tete, ni pied de page, ni fond,
 * pour etre ajoutee comme source navigateur d'OBS. La cle d'overlay arrive dans le fragment de l'adresse
 * (`#cle=...`), que la page lit puis efface (`overlay/cle.ts`) : elle n'est pas un parametre de recherche, que
 * le navigateur enverrait au serveur.
 */
export const overlayEssaiRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/overlay/essai",
  staticData: { pageNue: true },
  component: EssaiOverlay,
});

/**
 * L'overlay `scene` : la page nue et invisible qui tient la scene active d'OBS (`overlay/scene.tsx`). Meme
 * adresse que l'essai, avec `#cle=...`. Si le rendu levait, la page resterait vide : un message d'erreur de
 * l'app ne doit jamais passer a l'ecran du stream, d'ou `errorComponent` qui ne rend rien.
 */
export const overlaySceneRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/overlay/scene",
  staticData: { pageNue: true },
  component: SceneOverlay,
  errorComponent: () => null,
});

/**
 * L'overlay `taverne` : le QR code du stream et sa ligne, la seule page nue qui montre quelque chose a l'ecran
 * (`overlay/taverne.tsx`). Meme adresse que les autres, avec `#cle=...`. Un message d'erreur de l'app ne doit jamais
 * passer a l'ecran du stream : si le rendu levait, la page resterait vide, d'ou `errorComponent` qui ne rend rien.
 */
export const overlayTaverneRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/overlay/taverne",
  staticData: { pageNue: true },
  component: TaverneOverlay,
  errorComponent: () => null,
});

/**
 * Les reglages de la regie. L'index (`/regie/reglages`) lie Twitch (`/regie/reglages/twitch`), les overlays d'OBS
 * (`reglages/cle-overlay.tsx`), l'import de l'ancien Castellan (`reglages/import.tsx`) et la diffusion.
 */
export const reglagesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/regie/reglages",
  component: ReglagesPage,
});

export const reglagesOverlaysRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/regie/reglages/overlays",
  component: CleOverlay,
});

export const reglagesImportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/regie/reglages/import",
  component: ImportPage,
});

/**
 * La fiche d'un viewer en pleine page (`direct/fiche-viewer.tsx`) : l'ecran En direct l'ouvre en panneau au grand
 * ecran, et mene ici au telephone. L'identifiant vient de l'adresse, la fiche le valide avant tout appel.
 */
export const viewerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/regie/viewers/$id",
  component: ViewerRoute,
});

function ViewerRoute(): ReactElement {
  const { id } = viewerRoute.useParams();
  return <FicheViewer viewerId={id} />;
}

/** Le classement de la renommee (`viewers/classement.tsx`, ticket 2 du socle). */
export const classementRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/regie/renommee",
  component: ClassementPage,
});

/**
 * Les pages du site des viewers sont livrees a part, comme la regie : `/rejoindre` et `/moi` pesent ensemble pres de 60 ko du paquet
 * d'entree, que Vite signale au-dela de 500 ko. Le fichier arrive pendant l'attente (`ChargementDeLEntree`) ; un fichier qui n'arrive pas
 * (l'app a ete mise a jour depuis l'ouverture de l'onglet) recharge la page une fois, puis le dit. Ce que la route lit sans charger
 * vit dans `taverne/entree-texts.ts`, et `routes.test.tsx` verifie que rien d'autre n'est importe a la lecture.
 */
const RejoindrePage = lazy(() =>
  import("./taverne/rejoindre").then(
    (module) => ({ default: module.RejoindrePage }),
    (cause: unknown) => {
      throw new ChargementDeLEntreeEchoue(cause);
    },
  ),
);
const MoiPage = lazy(() =>
  import("./taverne/moi").then(
    (module) => ({ default: module.MoiPage }),
    (cause: unknown) => {
      throw new ChargementDeLEntreeEchoue(cause);
    },
  ),
);

/** Le fichier d'une page du site n'est pas arrive : la seule erreur que `ErreurDeLEntree` traite. */
class ChargementDeLEntreeEchoue extends Error {
  constructor(cause: unknown) {
    super("Chargement d'une page du site echoue", { cause });
    this.name = "ChargementDeLEntreeEchoue";
  }
}

/**
 * Ce que la page dit pendant que son fichier arrive. `tv-fond` peint le canevas derriere elle (`theme.css`) : une page nue n'a pas le
 * fond du template, et sans lui le navigateur montrerait son noir par defaut le temps du chargement.
 */
function ChargementDeLEntree(): ReactElement {
  return (
    <div className="tv-fond police-texte flex min-h-screen items-center justify-center bg-background text-[22px] text-[#FCDD8E]">
      <p role="status">{CHARGEMENT_DE_L_ENTREE}</p>
    </div>
  );
}

/** Comme `DiffusionIndisponible` : un rechargement tout seul, une fois, puis la phrase et le bouton. */
export function EntreeIndisponible({
  recharger = RECHARGER_LA_PAGE,
}: {
  recharger?: () => void;
}): ReactElement {
  // biome-ignore lint/correctness/useExhaustiveDependencies: une seule fois, au montage de l'ecran d'echec
  useEffect(() => {
    rechargerUneFois(recharger);
  }, []);
  return (
    <div className="tv-fond police-texte flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-4 text-center text-[22px] text-[#FCDD8E]">
      <p role="alert">{ENTREE_INDISPONIBLE}</p>
      <Button onClick={recharger}>{ENTREE_RECHARGER}</Button>
    </div>
  );
}

function ErreurDeLEntree({ error }: ErrorComponentProps): ReactElement {
  if (!(error instanceof ChargementDeLEntreeEchoue)) throw error;
  return <EntreeIndisponible />;
}

/**
 * Ce que l'adresse a mis dans un parametre : du texte. Le routeur lit les valeurs comme du JSON (`?connecte=1` donne le nombre 1,
 * `?error=404` aussi) : on ramene tout a du texte, que la page confronte ensuite a ses listes fermees.
 */
function texteDeLAdresse(valeur: unknown): string | undefined {
  if (typeof valeur === "string") return valeur;
  if (typeof valeur === "number" || typeof valeur === "boolean") return String(valeur);
  return undefined;
}

const SORTIES: readonly SortieDeRejoindre[] = ["delie", "delie-ici", "quitte"];
const sortieDe = (valeur: unknown): SortieDeRejoindre | null =>
  SORTIES.find((sortie) => sortie === valeur) ?? null;

/**
 * Ce que `/moi` a pose dans l'ETAT de l'historique en renvoyant la personne ici (`{ apres: "delie" | "delie-ici" | "quitte" }`). Une
 * valeur que la page ne connait pas ne dit rien : l'etat d'une entree n'est pas plus sur que l'adresse d'un lien.
 */
const sortieDeLEtat = (etat: unknown): SortieDeRejoindre | null =>
  typeof etat === "object" && etat !== null ? sortieDe((etat as { apres?: unknown }).apres) : null;

/**
 * `/rejoindre` : l'entree du chateau, la page que le QR code du stream ouvre (`taverne/rejoindre.tsx`). Une page nue : le template met
 * un en-tete et un pied de page sur toute page, et celle-ci a les siens (le logo, la place de la pastille, les liens legaux).
 * Trois codes y arrivent par l'adresse, lus UNE fois puis retires (un rechargement ne rejoue pas un refus) :
 * - `?error=<code>` : Better Auth ramene la personne ici quand le retour de Twitch echoue (`viewer_refuse`, `signup_closed`, ou un des
 *   codes de Better Auth que la page ne nomme pas) ;
 * - `?connecte=1` : la personne revient de Twitch, avec sa session ;
 * - `?suite=<chemin>` : ou aller une fois connecte. Seul un chemin local passe (`suite.ts`) ; il reste dans l'adresse.
 * Et un par l'ETAT de l'historique, jamais par l'adresse : `{ apres: "delie" | "delie-ici" | "quitte" }`, que `/moi` pose en renvoyant la
 * personne ici (la page dit ce qui s'est passe). Un lien ne peut pas le poser : `/rejoindre?apres=quitte` ne dit rien (tour 1 de la PR 6,
 * M11 : il affichait « Tu as quitte le chateau » a un visiteur qui n'avait rien fait).
 * L'adresse est a n'importe qui : rien de ce qu'elle dit n'est montre tel quel.
 */
export const rejoindreRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/rejoindre",
  staticData: { pageNue: true },
  errorComponent: ErreurDeLEntree,
  validateSearch: (
    search: Record<string, unknown>,
  ): { error?: string; connecte?: string; suite?: string } => {
    const error = texteDeLAdresse(search.error);
    const connecte = texteDeLAdresse(search.connecte);
    const suite = texteDeLAdresse(search.suite);
    return {
      ...(error === undefined ? {} : { error }),
      ...(connecte === undefined ? {} : { connecte }),
      ...(suite === undefined ? {} : { suite }),
    };
  },
  component: RejoindreRoute,
});

function RejoindreRoute(): ReactElement {
  const search = rejoindreRoute.useSearch();
  const navigate = rejoindreRoute.useNavigate();
  const router = useRouter();
  // Lu une fois, au montage : la page garde ce que l'adresse et l'etat ont dit, meme quand la route les retire.
  const [adresse] = useState<AdresseLue>(() => ({
    erreur: search.error === undefined || search.error === "" ? null : search.error,
    connecte: search.connecte === "1" || search.connecte === "true",
    sortie: sortieDeLEtat(router.state.location.state),
    suite: suiteSure(search.suite),
  }));
  const nettoyer = useCallback((): void => {
    // `search` remplace tout, `error_description` compris (le texte libre de Better Auth). `suite` reste. Sans `state`, le routeur
    // vide aussi l'etat de l'entree : `{ apres }` ne survit pas a un rechargement.
    void navigate({
      search: adresse.suite === null ? {} : { suite: adresse.suite },
      replace: true,
    });
  }, [navigate, adresse.suite]);
  const aller = useCallback((chemin: string): void => router.history.push(chemin), [router]);
  const remplacer = useCallback((chemin: string): void => router.history.replace(chemin), [router]);
  return (
    <Suspense fallback={<ChargementDeLEntree />}>
      <RejoindrePage
        adresse={adresse}
        contact={publishedContactEmail(appConfig)}
        aller={aller}
        remplacer={remplacer}
        nettoyer={nettoyer}
      />
    </Suspense>
  );
}

/**
 * `/moi` : l'identite, les telephones relies, les sorties (`taverne/moi.tsx`). Une page nue, avec la coquille du site des viewers
 * (en-tete, onglets, pied). Une personne qui n'est pas reliee, ou qui ne l'est plus, est ramenee sur `/rejoindre`, avec un bandeau quand
 * elle l'etait.
 */
export const moiRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/moi",
  staticData: { pageNue: true },
  errorComponent: ErreurDeLEntree,
  component: MoiRoute,
});

function MoiRoute(): ReactElement {
  const router = useRouter();
  const aller = useCallback((chemin: string): void => router.history.push(chemin), [router]);
  // REMPLACE l'entree de `/moi` (« Precedent » ne ramene pas sur un `/moi` delie) et dit ce qui s'est passe par l'etat de l'historique,
  // jamais par l'adresse : un lien ne peut pas poser `{ apres }`.
  const versRejoindre = useCallback(
    (sortie: SortieDeLaPage | null): void =>
      router.history.replace("/rejoindre", sortie === null ? undefined : { apres: sortie }),
    [router],
  );
  return (
    <Suspense fallback={<ChargementDeLEntree />}>
      <MoiPage
        contact={publishedContactEmail(appConfig)}
        aller={aller}
        versRejoindre={versRejoindre}
      />
    </Suspense>
  );
}

export const appRoutes = [
  homeRoute,
  rejoindreRoute,
  moiRoute,
  regieRoute,
  regieTwitchRoute,
  diffusionRoute,
  overlayEssaiRoute,
  overlaySceneRoute,
  overlayTaverneRoute,
  reglagesRoute,
  reglagesOverlaysRoute,
  reglagesImportRoute,
  recompensesRoute,
  viewerRoute,
  classementRoute,
  ...cartesRoutes,
  carteRoute,
  planningRoute,
  aPosterRoute,
];

/**
 * Une session qui glisse (decision 11 du plan du ticket 3, `session-glisse.ts`) : chaque page de l'app lit `GET /api/auth/get-session`
 * une fois a l'ouverture, **la regie comme le site**, sans quoi une session durerait 90 jours fermes depuis sa creation. Les pages
 * nues d'OBS n'ont pas de session et n'appellent rien. Une page de la regie n'importe rien de `taverne/` : c'est ce fichier qui enveloppe
 * son composant, apres coup.
 */
function avecLaSessionGlissante(Page: RouteComponent | undefined): () => ReactElement {
  const Contenu = (Page ?? Outlet) as ComponentType;
  return function PageQuiFaitGlisser(): ReactElement {
    useSessionGlissante();
    return <Contenu />;
  };
}

const PAGES_NUES_D_OBS: readonly AnyRoute[] = [
  overlayEssaiRoute,
  overlaySceneRoute,
  overlayTaverneRoute,
];

for (const route of appRoutes as readonly AnyRoute[]) {
  if (PAGES_NUES_D_OBS.includes(route)) continue;
  route.update({ component: avecLaSessionGlissante(route.options.component) });
}
